'use client'

import type { CartLine } from '@mist/contracts/commerce'
import { trackEvent } from '@/lib/analytics'
import { useCart } from '@/lib/cart'

/**
 * Adds one configured line to the bag and opens the drawer. The caller passes
 * the display title, the detail lines the drawer will show (for example
 * `Engraving: "For Sara"`), and the unit amount for display; the contract line
 * itself is what checkout later sends. Analytics records the choice as a
 * collection interaction by month/line or edition id only — engraving and
 * signature text never leave the bag.
 */
export type AddToCartButtonProps = {
  readonly line: CartLine
  readonly title: string
  readonly details: readonly string[]
  readonly unitAmountCents: number
  readonly label: string
  readonly className?: string
  readonly onAdded?: () => void
}

export default function AddToCartButton({ line, title, details, unitAmountCents, label, className, onAdded }: AddToCartButtonProps) {
  const { add, setOpen } = useCart()

  function onClick() {
    add({ ...line, title, details: [...details], unitAmountCents })
    setOpen(true)
    trackEvent('mist_collection_interaction', {
      route: window.location.pathname,
      collection_action: 'add_to_cart',
      ...(line.kind === 'bottle' && line.month ? { collection_month: line.month } : {}),
      ...(line.kind === 'bottle' && line.line ? { collection_line: line.line } : {}),
      ...(line.kind === 'edition' && line.editionId ? { edition_id: line.editionId } : {}),
    })
    onAdded?.()
  }

  return (
    <button type="button" className={className ?? 'mist-button mist-button--primary'} onClick={onClick} data-add-to-cart="">
      {label}
    </button>
  )
}
